import { z } from 'zod';
export const tasksTasksCapability = 'tasks.tasks.v1';
export const tasksAssignmentsCapability = 'tasks.assignments.v1';
export const tasksCommentsCapability = 'tasks.comments.v1';
export const tasksCommandIdSchema = z.string().uuid();
export const tasksTaskIdSchema = z.string().uuid();
export const tasksTaskStatusSchema = z.enum([
    'open', 'in_progress', 'blocked', 'done', 'cancelled',
]);
export const tasksTaskPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);
export const tasksCompletionRuleSchema = z.enum([
    'one_complete_for_all', 'all_assignees_signoff',
]);
export const tasksAssignmentSignoffStatusSchema = z.enum([
    'pending', 'approved', 'not_required',
]);
export const tasksCommentTypeSchema = z.enum(['comment', 'status_update', 'system']);
export const tasksProjectReferenceSchema = z.strictObject({
    projectId: z.string().uuid(),
    label: z.string().trim().min(1).max(500),
    projectRevision: z.number().int().positive(),
});
export const tasksAssignmentSchema = z.strictObject({
    membershipId: z.string().uuid(),
    requiresSignoff: z.boolean(),
    signoffStatus: tasksAssignmentSignoffStatusSchema,
    signedOffAt: z.string().datetime({ offset: true }).nullable(),
    signedOffByMembershipId: z.string().uuid().nullable(),
    assignedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const tasksCompletionSummarySchema = z.strictObject({
    rule: tasksCompletionRuleSchema,
    assigneeCount: z.number().int().nonnegative(),
    requiredCount: z.number().int().nonnegative(),
    signedCount: z.number().int().nonnegative(),
    pendingCount: z.number().int().nonnegative(),
    complete: z.boolean(),
});
export const tasksTaskSchema = z.strictObject({
    id: tasksTaskIdSchema,
    tenantId: z.string().uuid(),
    title: z.string().trim().min(1).max(240),
    description: z.string().max(10_000).nullable(),
    status: tasksTaskStatusSchema,
    priority: tasksTaskPrioritySchema,
    projectReference: tasksProjectReferenceSchema.nullable(),
    primaryAssignedMembershipId: z.string().uuid().nullable(),
    assignments: z.array(tasksAssignmentSchema).max(100),
    completionSummary: tasksCompletionSummarySchema,
    dueAt: z.string().datetime({ offset: true }).nullable(),
    blockedReason: z.string().max(240).nullable(),
    blockedDetail: z.string().max(2_000).nullable(),
    waitingOn: z.string().max(500).nullable(),
    nextChaseAt: z.string().datetime({ offset: true }).nullable(),
    estimatedMinutes: z.number().int().nonnegative().nullable(),
    sourceModuleId: z.string().regex(/^[a-z][a-z0-9-]*$/).nullable(),
    sourceRecordId: z.string().max(240).nullable(),
    revision: z.number().int().positive(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    completedByMembershipId: z.string().uuid().nullable(),
    cancelledAt: z.string().datetime({ offset: true }).nullable(),
    cancelledByMembershipId: z.string().uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
const tasksTaskMutationFields = {
    title: z.string().trim().min(1).max(240),
    description: z.string().trim().max(10_000).nullable(),
    priority: tasksTaskPrioritySchema,
    projectId: z.string().uuid().nullable(),
    assignedMembershipIds: z.array(z.string().uuid()).max(100),
    completionRule: tasksCompletionRuleSchema,
    dueAt: z.string().datetime({ offset: true }).nullable(),
    blockedReason: z.string().trim().max(240).nullable(),
    blockedDetail: z.string().trim().max(2_000).nullable(),
    waitingOn: z.string().trim().max(500).nullable(),
    nextChaseAt: z.string().datetime({ offset: true }).nullable(),
    estimatedMinutes: z.number().int().nonnegative().nullable(),
};
export const tasksCreateTaskSchema = z.strictObject({
    commandId: tasksCommandIdSchema,
    title: tasksTaskMutationFields.title,
    description: tasksTaskMutationFields.description.default(null),
    status: z.enum(['open', 'in_progress', 'blocked']).default('open'),
    priority: tasksTaskMutationFields.priority.default('normal'),
    projectId: tasksTaskMutationFields.projectId.default(null),
    assignedMembershipIds: tasksTaskMutationFields.assignedMembershipIds.default([]),
    completionRule: tasksTaskMutationFields.completionRule.default('one_complete_for_all'),
    dueAt: tasksTaskMutationFields.dueAt.default(null),
    blockedReason: tasksTaskMutationFields.blockedReason.default(null),
    blockedDetail: tasksTaskMutationFields.blockedDetail.default(null),
    waitingOn: tasksTaskMutationFields.waitingOn.default(null),
    nextChaseAt: tasksTaskMutationFields.nextChaseAt.default(null),
    estimatedMinutes: tasksTaskMutationFields.estimatedMinutes.default(null),
    sourceModuleId: z.string().regex(/^[a-z][a-z0-9-]*$/).nullable().default(null),
    sourceRecordId: z.string().trim().min(1).max(240).nullable().default(null),
}).superRefine((task, context) => {
    if ((task.sourceModuleId === null) !== (task.sourceRecordId === null)) {
        context.addIssue({
            code: 'custom',
            path: ['sourceRecordId'],
            message: 'Source module and record identity must be supplied together.',
        });
    }
    if (task.status === 'blocked' && task.blockedReason === null && task.blockedDetail === null) {
        context.addIssue({
            code: 'custom',
            path: ['blockedReason'],
            message: 'A blocked task requires a reason or detail.',
        });
    }
});
export const tasksUpdateTaskSchema = z.strictObject({
    commandId: tasksCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    title: tasksTaskMutationFields.title.optional(),
    description: tasksTaskMutationFields.description.optional(),
    status: z.enum(['open', 'in_progress', 'blocked']).optional(),
    priority: tasksTaskMutationFields.priority.optional(),
    projectId: tasksTaskMutationFields.projectId.optional(),
    assignedMembershipIds: tasksTaskMutationFields.assignedMembershipIds.optional(),
    completionRule: tasksTaskMutationFields.completionRule.optional(),
    dueAt: tasksTaskMutationFields.dueAt.optional(),
    blockedReason: tasksTaskMutationFields.blockedReason.optional(),
    blockedDetail: tasksTaskMutationFields.blockedDetail.optional(),
    waitingOn: tasksTaskMutationFields.waitingOn.optional(),
    nextChaseAt: tasksTaskMutationFields.nextChaseAt.optional(),
    estimatedMinutes: tasksTaskMutationFields.estimatedMinutes.optional(),
}).superRefine((task, context) => {
    if (Object.keys(task).every((key) => ['commandId', 'expectedRevision'].includes(key))) {
        context.addIssue({ code: 'custom', message: 'At least one task field must change.' });
    }
    if (task.status === 'blocked' && task.blockedReason === null && task.blockedDetail === null) {
        context.addIssue({
            code: 'custom',
            path: ['blockedReason'],
            message: 'A blocked task requires a reason or detail.',
        });
    }
});
export const tasksSignOffTaskSchema = z.strictObject({
    commandId: tasksCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    completeForAll: z.boolean().default(false),
});
export const tasksCompleteUnassignedTaskSchema = z.strictObject({
    commandId: tasksCommandIdSchema,
    expectedRevision: z.number().int().positive(),
});
export const tasksReopenTaskSchema = z.strictObject({
    commandId: tasksCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(2_000),
});
export const tasksCancelTaskSchema = z.strictObject({
    commandId: tasksCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(2_000),
});
export const tasksCreateCommentSchema = z.strictObject({
    commandId: tasksCommandIdSchema,
    body: z.string().trim().min(1).max(10_000),
    commentType: z.enum(['comment', 'status_update']).default('comment'),
});
export const tasksTaskListQuerySchema = z.strictObject({
    scope: z.enum(['mine', 'all']).default('mine'),
    status: tasksTaskStatusSchema.optional(),
    projectId: z.string().uuid().optional(),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const tasksTaskCommentSchema = z.strictObject({
    id: z.string().uuid(),
    taskId: tasksTaskIdSchema,
    body: z.string().min(1).max(10_000),
    commentType: tasksCommentTypeSchema,
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const tasksTaskResponseSchema = z.strictObject({
    data: tasksTaskSchema,
});
export const tasksTaskCommandResponseSchema = z.strictObject({
    data: tasksTaskSchema,
    idempotent: z.boolean(),
});
export const tasksTasksResponseSchema = z.strictObject({
    data: z.array(tasksTaskSchema),
    counts: z.record(tasksTaskStatusSchema, z.number().int().nonnegative()),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    }),
});
export const tasksTaskCommentsResponseSchema = z.strictObject({
    data: z.array(tasksTaskCommentSchema),
});
export const tasksTaskCommentResponseSchema = z.strictObject({
    data: tasksTaskCommentSchema,
    idempotent: z.boolean().default(false),
});
//# sourceMappingURL=tasks-task.contract.js.map